<template>
  <div class="nylon-DimensionFields">
    <div class="nylon-DimensionFields-liner">
      <div class="dimensions">
        <div class="dimensions-subgroup">
          <div class="dimension">
            <h5 class="dimension-title">Width</h5>
            <div class="dimension-field">
              <input type="number" :min="customSize.min.width" :max="customSize.max.width" :step="customSize.step" v-model="geometry.width" @input="$emit('width-input', $event)" />
              <span class="dimension-symbol">in.</span>
            </div>
          </div>
          <div class="dimension">
            <h5 class="dimension-title">Height</h5>
            <div class="dimension-field">
              <input type="number" :min="customSize.min.height" :max="customSize.max.height" :step="customSize.step" v-model="geometry.height" @input="$emit('height-input', $event)" />
              <span class="dimension-symbol">in.</span>
            </div>
          </div>
        </div>
        <div class="dimensions-subgroup" v-if="showOffset">
          <div class="dimension" v-if="showOffsetX">
            <h5 class="dimension-title">Offset X</h5>
            <div class="dimension-field">
              <input type="number" :min="offsetRange.min.x" :max="offsetRange.max.x" :step="offsetRange.step" v-model="offset.x" @input="$emit('offset-x-input', $event)" />
              <span class="dimension-symbol">in.</span>
            </div>
          </div>
          <div class="dimension" :class="{ 'is-disabled': disableOffsetY }">
            <h5 class="dimension-title">Offset Y</h5>
            <div class="dimension-field">
              <input type="number" :min="offsetRange.min.y" :max="offsetRange.max.y" :step="offsetRange.step" :disabled="disableOffsetY" v-model="offset.y" @input="$emit('offset-y-input', $event)" />
              <span class="dimension-symbol">in.</span>
            </div>
          </div>
        </div>
        <p class="dimensions-tip">{{ tip }}</p>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Nylon — DimensionFields
 *
 * The advanced custom-dimension block: a Width / Height pair, plus an
 * optional Offset X / Y pair, plus a cautionary tip. Two consumers
 * share this exact chrome:
 *   1. `<NylonPlacementOptions>` (submission wizard BUILDER + account
 *      edit-product step 2) — shows it when a custom placement is
 *      selected, with the Offset X / Y subgroup for APPAREL
 *      non-embroidery.
 *   2. STICKER custom-size inside `<NylonColorStylePanel>` — shows Width /
 *      Height only (no offsets), with a shorter tip.
 *
 * The intentional differences between the two render the same primitive
 * with props rather than two copies of the markup:
 *   - `showOffset` gates the Offset X / Y subgroup (wizard APPAREL only).
 *   - `tip` carries the cautionary text; the default includes the
 *     "use custom offsets only…" sentence the wizard shows, STICKER
 *     passes the shorter variant without it.
 *
 * Two-channel input binding (mirrors the pre-extraction markup exactly):
 * each input `v-model`s a nested property of the `geometry` / `offset`
 * object passed by reference (mutated in place for instant display), and
 * ALSO emits the raw `$event` so the consumer's debounced mixin handler
 * can clamp + write the real geometry. The consumer decides what extra
 * payload to attach when it re-emits upward (STICKER threads the color's
 * geometry objects through; the wizard does not).
 *
 * Root class is `.nylon-DimensionFields` (+ `-liner`), renamed from
 * `.placement-options-advanced` in the 2026-06-18 color-system pass;
 * `.dimensions*` descendants stay generic (scoped under the root). The
 * universal chrome lives in `_NylonDimensionFields.scss` (no per-context
 * ramp), with a `.nylon-ColorStylePanel-chosenColorBlanks .nylon-DimensionFields` override for the
 * STICKER-embedded case.
 *
 * Props:
 *   geometry   (Object) — { width, height }; v-modeled, mutated in place
 *   customSize (Object) — { min: {width,height}, max: {width,height}, step }
 *   showOffset (Boolean)— render the Offset X / Y subgroup
 *   offset     (Object) — { x, y }; required when showOffset is true
 *   disableOffsetY (Boolean) — grey out + disable the Offset Y input (back
 *     print's Collar print positions at the neck, so a Y offset is moot)
 *   showOffsetX (Boolean) — render the Offset X input (a hat or beanie
 *     nudges vertically only, PRO-1011)
 *   offsetRange (Object) — { min: {x,y}, max: {x,y}, step } for the offset
 *     inputs; ±1in by default, a hat's or beanie's from getOffsetRange()
 *   tip        (String) — cautionary text; default includes the offset sentence
 *
 * Emits:
 *   width-input ($event), height-input ($event),
 *   offset-x-input ($event), offset-y-input ($event) — raw input events
 *     for the consumer's debounced handlers
 */
const DEFAULT_TIP = "🐉 Careful! Here be dragons. Use custom offsets only if your design requires optical centering. Width and height controls artwork container dimensions not the artwork itself.";

export default {
  name: "NylonDimensionFields",
  props: {
    geometry: { type: Object, required: true },
    customSize: { type: Object, required: true },
    showOffset: { type: Boolean, default: false },
    offset: { type: Object, default: null },
    disableOffsetY: { type: Boolean, default: false },
    showOffsetX: { type: Boolean, default: true },
    offsetRange: { type: Object, default: () => ({ min: { x: -1, y: -1 }, max: { x: 1, y: 1 }, step: 0.1 }) },
    tip: { type: String, default: DEFAULT_TIP },
  },
  emits: ["width-input", "height-input", "offset-x-input", "offset-y-input"],
};
</script>
